import { toast } from "@birdwatch/ui/toast";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { cn } from "cn";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PlaceTypeIcon } from "@/components/ui/status/place-type-icon";
import { APP_ROUTERS } from "@/constants/app-routers";
import { renderPlaceAddress } from "@/features/my/_libs/utils";
import { useMyPlaceContext } from "@/features/my/_providers/my-place-context-provider";
import type { MyPlaceListItem } from "@/features/my/_types";
import { ListTab } from "@/features/my/_types";
import { useQueryState } from "@/hooks/use-query-state";
import type { PlaceType } from "@/types/openapi-enum";

export type PlaceSwitcherItemProps = {
  place: MyPlaceListItem;
  isActive: boolean;
  onClick: () => void;
};

export function PlaceSwitcherItem({
  place,
  isActive,
  onClick,
}: PlaceSwitcherItemProps) {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  // Route-agnostic read: this renders across several `my/*` routes whose `tab`
  // enums differ, so the value is narrowed per target below.
  const [tab] = useQueryState("tab");
  const { switchPlace } = useMyPlaceContext();

  // Preserve current route structure using matchRoute
  const navigateForSwitch = () => {
    if (matchRoute({ to: APP_ROUTERS.MY_HOME })) {
      return navigate({
        to: APP_ROUTERS.MY_HOME,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.MY_LIST })) {
      return navigate({
        to: APP_ROUTERS.MY_LIST,
        search:
          tab &&
          [ListTab.IN_PROGRESS, ListTab.COMPLETED].includes(tab as ListTab)
            ? { tab: tab as ListTab.IN_PROGRESS | ListTab.COMPLETED }
            : undefined,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.MY_RECORDS })) {
      return navigate({
        to: APP_ROUTERS.MY_RECORDS,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.MY_BIRDIE })) {
      // Birdie's chat keeps its conversation across the switch; its next message names the new home.
      return navigate({
        to: APP_ROUTERS.MY_BIRDIE,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.MY_ORDERS })) {
      return navigate({
        to: APP_ROUTERS.MY_HOME,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.MY_ONBOARDING })) {
      return navigate({
        to: APP_ROUTERS.MY_ONBOARDING,
        replace: true,
      });
    }
    if (matchRoute({ to: APP_ROUTERS.PRESALE_SUCCESS })) {
      return navigate({
        to: APP_ROUTERS.PRESALE_SUCCESS,
        replace: true,
      });
    }
    // Default to home
    return navigate({
      to: APP_ROUTERS.MY_HOME,
      replace: true,
    });
  };

  const handleClick = () => {
    onClick();
    if (isActive) return;

    void switchPlace(place.id, async () => {
      await navigateForSwitch();
      toast.success({
        title: `Switched to ${renderPlaceAddress(place)}!`,
      });
    });
  };

  return (
    <label
      className={cn(
        /* sorted */
        "flex",
        "items-center gap-5",
        "px-6",
        "rounded-lg",
        {
          "hover:bg-bg-dark-min transition-all duration-100 cursor-pointer":
            !isActive,
        },
      )}
      onClick={handleClick}
      data-test="place-switcher-item"
    >
      <PlaceTypeIcon
        type={place.type as PlaceType}
        size={24}
        className="text-text-primary"
      />
      <div className="flex items-center justify-between flex-1 py-3 border-b border-border-dark-min">
        <div>
          <div className="text-base text-text-dark">
            {renderPlaceAddress(place)}
          </div>
          <div className="text-sm text-text-dark-high">
            {renderPlaceAddress(place, ["city", "state", "zipcode"])}
          </div>
        </div>
        <RadioGroup value={isActive ? place.id : ""}>
          <RadioGroupItem value={place.id} />
        </RadioGroup>
      </div>
    </label>
  );
}
